/**
 * Tool factories for the inline kernel-edit chat. The agent uses
 * these to mutate the in-progress kernel draft (steps, render spec,
 * metadata) and to execute the draft for previews.
 *
 * Draft state lives in React state owned by KernelDetailSplit. The
 * tool factory receives `getDraft` + `setDraft` thunks so each
 * tool call reads + writes through the SAME state the editor UI
 * displays — the user sees the agent's changes in the editor as
 * they happen.
 *
 * Why a family of tools instead of one "edit_kernel(json)" omnibus
 * tool: each operation has a distinct shape and intent. The agent
 * reasons about what it's doing (renaming a step vs. swapping the
 * render kind) and the result UI for each tool is more legible. The
 * raw-JSON escape hatch still exists for power-user multi-line
 * edits — the agent can call `inspect_kernel` to see the whole thing.
 */

import { Type } from "@earendil-works/pi-ai";
import { SPEC_DOC, KNOWN_RENDER_KINDS } from "./kernelPrimitiveSpecs.js";
import {
    formatRenderIssues,
    inspectKernelRender,
    validateRenderSpec,
} from "./kernelRenderValidation.js";
import { runKernel } from "./kernelRunner.js";
import { runKernelExtraction, summariseRecentMessages } from "./kernelSaveAgent.js";
import { kernelErrorMessageForCurrentUser } from "./kernelErrorCopy.js";
import {
    APPROVED_PYTHON_PACKAGE_NAMES,
    normalizePythonPackageRequirements,
} from "./pythonPackageRequirements.js";
import {
    asResolvedKernel,
    asRunContext,
    mapResolvedKernel,
} from "./kernelResolver.js";

// ── Shared helpers ───────────────────────────────────────────────

function nextStepId(steps, prefix = "s") {
    let n = 1;
    const used = new Set((steps || []).map((s) => s.id));
    while (used.has(`${prefix}${n}`)) n += 1;
    return `${prefix}${n}`;
}

function summariseStep(step, idx) {
    if (!step) return `#${idx + 1} (missing)`;
    if (step.kind === "python") {
        const lines = (step.code || "").split("\n").length;
        const inputCount = step.inputs ? Object.keys(step.inputs).length : 0;
        const packages = Array.isArray(step.packages) && step.packages.length > 0
            ? ` · packages=${step.packages.join(",")}`
            : "";
        return `#${idx + 1} python · id=${step.id} · ${lines} line${lines === 1 ? "" : "s"} · ${inputCount} input${inputCount === 1 ? "" : "s"}${packages}`;
    }
    if (step.kind === "analytics_query") {
        const dim = step.spec?.dim || step.spec?.root?.entity || "—";
        return `#${idx + 1} analytics_query · id=${step.id} · ${dim}`;
    }
    return `#${idx + 1} ${step.kind || "?"} · id=${step.id}`;
}

function summariseKernel(k) {
    const stepCount = Array.isArray(k.steps) ? k.steps.length : 0;
    return {
        command: k.command,
        title: k.title,
        description: k.description || null,
        stepCount,
        steps: (k.steps || []).map((s, i) => summariseStep(s, i)),
        render: { kind: k.render?.kind || null },
        renderSource: k.renderSource || null,
        params: k.params ? Object.keys(k.params) : [],
    };
}

// Common error shape — keeps every tool returning the same retry-able
// envelope so the agent's recovery loop can pattern-match cleanly.
function errResult(name, message, details = {}) {
    return {
        content: [{ type: "text", text: `${name}: ${message}` }],
        details: { error: message, ...details },
        isError: true,
    };
}

function okResult(name, text, details = {}) {
    return {
        content: [{ type: "text", text: `${name}: ${text}` }],
        details,
    };
}

// Mutations return the updated kernel summary in their result text
// so the agent always has the latest state in its conversation
// context — no need for it to call inspect_kernel between edits.
// This mirrors how main-chat tools surface their effects (the new
// rows are right there in the tool result the model just read).
function okResultWithKernel(name, text, k, details = {}) {
    const summary = summariseKernel(k);
    return {
        content: [{
            type: "text",
            text: `${name}: ${text}\n\nkernel after edit:\n${JSON.stringify(summary, null, 2)}`,
        }],
        details: { ...details, kernel: k, summary },
    };
}

// ── Tools ────────────────────────────────────────────────────────

export function createInspectKernelTool({ getDraft }) {
    return {
        name: "inspect_kernel",
        label: "Inspect kernel",
        description:
            "RARELY NEEDED — recovery tool only. The current kernel state is already in your context: the system prompt has an initial snapshot, and every mutation tool (add_step, update_step, …) returns the updated kernel summary in its result. Use this tool ONLY if you genuinely suspect your view is stale (e.g. the user just said they edited the JSON manually). Calling it as a default first step is a known anti-pattern that ladders into loops.",
        parameters: Type.Object({}),
        async execute() {
            const k = getDraft();
            return {
                content: [{ type: "text", text: `current draft:\n${JSON.stringify(summariseKernel(k), null, 2)}` }],
                details: { kernel: k, summary: summariseKernel(k) },
            };
        },
    };
}

export function createUpdateMetaTool({ getDraft, setDraft }) {
    return {
        name: "update_meta",
        label: "Update kernel metadata",
        description:
            "Update top-level kernel metadata (title, description, params). Pass only the fields you want to change; omitted fields stay as-is. Does NOT change steps or the render spec — use the dedicated tools for those.",
        parameters: Type.Object({
            title: Type.Optional(Type.String({ description: "New human-readable title (one line)." })),
            description: Type.Optional(Type.String({
                description: "New description. Pass an empty string to clear.",
            })),
            params: Type.Optional(Type.Unknown({
                description: "Full replacement params map: { paramName: { type: 'number'|'string'|'boolean', default?: any } }. Use when you want to add/remove/retype parameters.",
            })),
        }),
        async execute(_id, { title, description, params }) {
            const k = getDraft();
            const next = { ...k };
            if (typeof title === "string") next.title = title;
            if (typeof description === "string") next.description = description || null;
            if (params && typeof params === "object") next.params = params;
            setDraft(next);
            return okResultWithKernel("update_meta", `updated ${[
                typeof title === "string" ? "title" : null,
                typeof description === "string" ? "description" : null,
                params && typeof params === "object" ? "params" : null,
            ].filter(Boolean).join(", ") || "no fields"}`, next, { op: "update_meta" });
        },
    };
}

export function createUpdateStepTool({ getDraft, setDraft }) {
    return {
        name: "update_step",
        label: "Update step",
        description:
            "Patch a single existing step. Identify the step by `stepId`. Pass only the fields you want to change — omitted fields stay as-is.\n\n" +
            "For an analytics_query step, the editable fields are `id` (rename) and `spec` (the query JSON).\n" +
            "For a python step: `id`, `code` (string), `inputs` (object mapping local variable name → another step's id), and `packages` (the declared numpy/pandas/scipy roots imported by the code).\n\n" +
            "If you're renaming a step (`id` change), this tool also retargets `renderSource` and any other step's `inputs` that pointed at the old id, so the graph stays connected.",
        parameters: Type.Object({
            stepId: Type.String({ description: "Current id of the step to patch (case-sensitive)." }),
            id: Type.Optional(Type.String({ description: "New id for the step. Other references that pointed at the old id will be retargeted automatically." })),
            spec: Type.Optional(Type.Unknown({ description: "New analytics_query spec (full replacement). Ignored if the step is python." })),
            code: Type.Optional(Type.String({ description: "New python code (full replacement). Ignored if the step is analytics_query." })),
            inputs: Type.Optional(Type.Unknown({ description: "New inputs mapping for python steps: { variableName: 'stepId' }. Ignored for analytics_query." })),
            packages: Type.Optional(Type.Array(
                Type.Union(APPROVED_PYTHON_PACKAGE_NAMES.map((name) => Type.Literal(name))),
                { description: "Declared Python package roots imported by this step." },
            )),
        }),
        async execute(_id, {
            stepId,
            id: newId,
            spec,
            code,
            inputs,
            packages,
        }) {
            const k = getDraft();
            const steps = k.steps || [];
            const idx = steps.findIndex((s) => s.id === stepId);
            if (idx < 0) return errResult("update_step", `no step with id "${stepId}"`, { available: steps.map((s) => s.id) });
            const old = steps[idx];
            const updated = { ...old };
            const idChanged = typeof newId === "string" && newId !== old.id;
            if (idChanged) updated.id = newId;
            if (old.kind === "analytics_query" && spec !== undefined) updated.spec = spec;
            if (old.kind === "python") {
                if (typeof code === "string") updated.code = code;
                if (inputs !== undefined) updated.inputs = inputs || {};
                if (packages !== undefined) {
                    try {
                        updated.packages = normalizePythonPackageRequirements(packages);
                    } catch {
                        return errResult(
                            "update_step",
                            "packages must be unique approved names: numpy, pandas, scipy",
                        );
                    }
                }
            }
            // Apply this step's edit + retarget any cross-references
            // that pointed at the old id so the kernel graph stays valid.
            const nextSteps = steps.map((s, i) => {
                if (i === idx) return updated;
                if (idChanged && s.kind === "python" && s.inputs && typeof s.inputs === "object") {
                    const remapped = {};
                    let changed = false;
                    for (const [k2, v] of Object.entries(s.inputs)) {
                        if (v === old.id) { remapped[k2] = newId; changed = true; }
                        else remapped[k2] = v;
                    }
                    return changed ? { ...s, inputs: remapped } : s;
                }
                return s;
            });
            const renderSource = idChanged && k.renderSource === old.id ? newId : k.renderSource;
            const next = { ...k, steps: nextSteps, renderSource };
            setDraft(next);
            return okResultWithKernel("update_step", `updated step ${stepId}${idChanged ? ` → ${newId}` : ""}`, next, { op: "update_step", stepId, newId: newId || null });
        },
    };
}

export function createAddStepTool({ getDraft, setDraft }) {
// Bind the step a new python step will almost always read from: the one it is
// being inserted after.
//
// Step outputs reach python only through the `inputs` dict, and a step that
// declares none can see nothing. Writing `{}` here -- the previous default --
// therefore produced a step whose code could not reach a single prior result,
// and the author has no way to see that from the tool schema, which calls
// inputs "Optional". The run dies on a NameError the author then has to guess
// its way out of, one full re-run per guess.
//
// The binding is written into the definition rather than inferred at run time.
// An empty map already means "no dependencies" for every step that has one, and
// it does not survive a cloud round-trip as distinct from an absent property, so
// there is nothing at run time that could safely tell the two apart. Explicit
// also keeps the payload honest: what the step ships to the realm is visible and
// editable, instead of quietly growing with every step added before it.
//
// Only the immediately preceding step. Binding every prior output would be
// forgiving but would serialise unrelated results into the realm on every run.
function defaultPythonStepInputs(steps, afterStepId) {
    if (!steps.length) return {};
    const prior = afterStepId
        ? steps[steps.findIndex((s) => s.id === afterStepId)]
        : steps[steps.length - 1];
    return prior?.id ? { [prior.id]: prior.id } : {};
}

    return {
        name: "add_step",
        label: "Add step",
        description:
            "Insert a new step into the kernel. Specify `kind` (analytics_query or python). Optionally specify where to insert via `afterStepId` (defaults to end) and a custom `id`. Returns the new step's id.\n\n" +
            "For python steps, provide `code` (string), `inputs` (object mapping local name → prior step id), and `packages` for each imported numpy/pandas/scipy root. For analytics_query, provide `spec` (the query JSON).",
        parameters: Type.Object({
            kind: Type.Union([Type.Literal("analytics_query"), Type.Literal("python")], {
                description: "Step kind.",
            }),
            id: Type.Optional(Type.String({ description: "Custom id for the new step. If omitted, an unused id like 's1', 's2' is generated." })),
            afterStepId: Type.Optional(Type.String({ description: "Insert after this step's id. Omit to append at the end." })),
            spec: Type.Optional(Type.Unknown({ description: "Required for analytics_query — the query spec JSON." })),
            code: Type.Optional(Type.String({ description: "Required for python — the code to execute. Set `result = …` to produce output." })),
            inputs: Type.Optional(Type.Unknown({ description: "Optional for python — { variableName: 'stepId' } mapping." })),
            packages: Type.Optional(Type.Array(
                Type.Union(APPROVED_PYTHON_PACKAGE_NAMES.map((name) => Type.Literal(name))),
                { description: "Declared Python package roots imported by this step." },
            )),
        }),
        async execute(_id, {
            kind,
            id,
            afterStepId,
            spec,
            code,
            inputs,
            packages,
        }) {
            const k = getDraft();
            const steps = (k.steps || []).slice();
            const newId = id && typeof id === "string" ? id : nextStepId(steps);
            if (steps.some((s) => s.id === newId)) {
                return errResult("add_step", `step id "${newId}" already exists. Pick a different id or omit it to auto-generate.`, { existing: steps.map((s) => s.id) });
            }
            let step;
            if (kind === "python") {
                let normalizedPackages;
                try {
                    normalizedPackages = normalizePythonPackageRequirements(packages);
                } catch {
                    return errResult(
                        "add_step",
                        "packages must be unique approved names: numpy, pandas, scipy",
                    );
                }
                step = {
                    kind: "python",
                    id: newId,
                    code: typeof code === "string" ? code : "result = None\n",
                    inputs: inputs && typeof inputs === "object"
                        ? inputs
                        : defaultPythonStepInputs(steps, afterStepId),
                    ...(normalizedPackages.length > 0
                        ? { packages: normalizedPackages }
                        : {}),
                };
            } else if (kind === "analytics_query") {
                if (!spec || typeof spec !== "object") {
                    return errResult("add_step", "analytics_query step needs a `spec` JSON object", {});
                }
                step = { kind: "analytics_query", id: newId, spec };
            } else {
                return errResult("add_step", `unknown kind "${kind}"`, {});
            }
            let insertAt = steps.length;
            if (afterStepId) {
                const idx = steps.findIndex((s) => s.id === afterStepId);
                if (idx < 0) return errResult("add_step", `no step with id "${afterStepId}" to insert after`, { available: steps.map((s) => s.id) });
                insertAt = idx + 1;
            }
            steps.splice(insertAt, 0, step);
            const next = { ...k, steps };
            setDraft(next);
            return okResultWithKernel("add_step", `added ${kind} step "${newId}" at position ${insertAt + 1}`, next, { op: "add_step", id: newId, position: insertAt });
        },
    };
}

export function createRemoveStepTool({ getDraft, setDraft }) {
    return {
        name: "remove_step",
        label: "Remove step",
        description:
            "Delete a step by id. Returns the remaining step ids. Note: any other step's `inputs` that referenced the removed id will be left dangling — the agent should follow up with `update_step` to fix consumers, or remove them too.",
        parameters: Type.Object({
            stepId: Type.String({ description: "Id of the step to delete." }),
        }),
        async execute(_id, { stepId }) {
            const k = getDraft();
            const steps = k.steps || [];
            const idx = steps.findIndex((s) => s.id === stepId);
            if (idx < 0) return errResult("remove_step", `no step with id "${stepId}"`, { available: steps.map((s) => s.id) });
            const nextSteps = steps.filter((_, i) => i !== idx);
            // Clear renderSource if it pointed at the removed step.
            const renderSource = k.renderSource === stepId ? null : k.renderSource;
            const next = { ...k, steps: nextSteps, renderSource };
            setDraft(next);
            return okResultWithKernel("remove_step", `removed "${stepId}"; ${nextSteps.length} step${nextSteps.length === 1 ? "" : "s"} remaining`, next, {
                op: "remove_step",
                removed: stepId,
                remaining: nextSteps.map((s) => s.id),
            });
        },
    };
}

export function createMoveStepTool({ getDraft, setDraft }) {
    return {
        name: "move_step",
        label: "Reorder step",
        description:
            "Move a step to a new position in the execution order. Useful when a step needs to run before/after another. Position is the new 1-based index.",
        parameters: Type.Object({
            stepId: Type.String({ description: "Id of the step to move." }),
            position: Type.Number({ description: "New 1-based position in the steps array (1 = first, length = last)." }),
        }),
        async execute(_id, { stepId, position }) {
            const k = getDraft();
            const steps = (k.steps || []).slice();
            const idx = steps.findIndex((s) => s.id === stepId);
            if (idx < 0) return errResult("move_step", `no step with id "${stepId}"`, { available: steps.map((s) => s.id) });
            const targetIdx = Math.max(0, Math.min(steps.length - 1, Math.round(position) - 1));
            if (targetIdx === idx) return okResult("move_step", `step "${stepId}" already at position ${position}`, { op: "move_step" });
            const [moved] = steps.splice(idx, 1);
            steps.splice(targetIdx, 0, moved);
            const next = { ...k, steps };
            setDraft(next);
            return okResultWithKernel("move_step", `moved "${stepId}" to position ${targetIdx + 1}`, next, { op: "move_step", stepId, position: targetIdx + 1 });
        },
    };
}

export function createUpdateRenderTool({ getDraft, setDraft }) {
    return {
        name: "update_render",
        label: "Update render spec",
        description:
            "Replace the kernel's render spec — the part that turns the final step's output into a visual primitive. Must include `kind` (e.g. \"chart\", \"stat\", \"bars\", \"table\"). Use `update_render_source` to choose which step's output the render consumes.\n\n" +
            SPEC_DOC,
        parameters: Type.Object({
            render: Type.Unknown({ description: "Full render spec object (kind + per-kind fields). Replaces the existing render." }),
        }),
        async execute(_id, { render }) {
            if (!render || typeof render !== "object" || Array.isArray(render)) {
                return errResult("update_render", "render must be a JSON object with at least a `kind` field", {});
            }
            if (typeof render.kind !== "string" || !render.kind) {
                return errResult("update_render", "render.kind is required and must be a string", {});
            }
            if (!KNOWN_RENDER_KINDS.has(render.kind)) {
                return errResult("update_render", `unknown render kind "${render.kind}"`, { valid: [...KNOWN_RENDER_KINDS] });
            }
            const issues = validateRenderSpec(render);
            if (issues.length) {
                return errResult("update_render", formatRenderIssues(issues), { issues });
            }
            const k = getDraft();
            const next = { ...k, render };
            setDraft(next);
            return okResultWithKernel("update_render", `render kind now "${render.kind}"`, next, { op: "update_render", kind: render.kind });
        },
    };
}

export function createUpdateRenderSourceTool({ getDraft, setDraft }) {
    return {
        name: "update_render_source",
        label: "Bind render to step",
        description:
            "Set which step's output feeds the render. Pass null or an empty string to clear (the kernel won't render until set again).",
        parameters: Type.Object({
            stepId: Type.Optional(Type.String({ description: "Id of the step whose output should drive the render. Pass empty string to clear." })),
        }),
        async execute(_id, { stepId }) {
            const k = getDraft();
            const next = stepId || null;
            if (next && !(k.steps || []).some((s) => s.id === next)) {
                return errResult("update_render_source", `no step with id "${next}"`, { available: (k.steps || []).map((s) => s.id) });
            }
            const nextK = { ...k, renderSource: next };
            setDraft(nextK);
            return okResultWithKernel("update_render_source", next ? `render source bound to "${next}"` : "render source cleared", nextK, { op: "update_render_source", renderSource: next });
        },
    };
}

export function createRunKernelTool({
    getDraft,
    resolvedDefinition = null,
    runContext = null,
}) {
    return {
        name: "run_kernel",
        label: "Run kernel",
        description:
            "Execute the current draft against live data. Steps run in order, params are substituted, and the final render spec's source step's output is returned. Use this to verify a change works before telling the user it's done — agents that edit blindly often break things. Returns the renderData (what the render would receive) plus per-step outputs so you can debug.",
        parameters: Type.Object({
            params: Type.Optional(Type.Unknown({
                description: "Optional parameter bindings (object). Keys must match the kernel's declared params; values can be any JSON-serialisable type. Omitted params use their declared defaults.",
            })),
        }),
        async execute(_id, { params }) {
            const k = getDraft();
            try {
                const currentDefinition = resolvedDefinition
                    ? mapResolvedKernel(resolvedDefinition, k)
                    : asResolvedKernel(k);
                const result = await runKernel({
                    resolvedDefinition: currentDefinition,
                    runContext: asRunContext(runContext),
                    params: params && typeof params === "object" ? params : {},
                });
                if (!result.ok) {
                    const errMsg = await kernelErrorMessageForCurrentUser(result.error, "kernel run failed");
                    return errResult("run_kernel", errMsg, { outputs: serializeOutputs(result.outputs) });
                }
                const renderValidation = inspectKernelRender(
                    result.renderSpec || k.render,
                    result.renderData,
                );
                if (renderValidation.status === "invalid_render") {
                    return errResult(
                        "run_kernel",
                        `render validation failed: ${formatRenderIssues(renderValidation.issues)}`,
                        {
                            stage: "render",
                            renderStatus: renderValidation.status,
                            issues: renderValidation.issues,
                            renderData: previewValue(result.renderData),
                            outputs: serializeOutputs(result.outputs),
                        },
                    );
                }
                // Trim the rendered data + per-step outputs so the
                // agent gets enough to reason about without flooding
                // context with multi-MB row sets.
                const summary = {
                    renderKind: result.renderSpec?.kind || null,
                    renderStatus: renderValidation.status,
                    renderData: previewValue(result.renderData),
                    outputs: serializeOutputs(result.outputs),
                };
                return {
                    content: [{ type: "text", text: `run_kernel: ok\n${JSON.stringify(summary, null, 2)}` }],
                    details: { op: "run_kernel", ...summary },
                };
            } catch (err) {
                const message = await kernelErrorMessageForCurrentUser(err, "kernel run failed");
                return errResult("run_kernel", message, {});
            }
        },
    };
}

// commit_to_kernel — the primary build path. The agent runs its
// analysis exactly like the main chat (analytics_query / execute_python
// / render_block), then calls this tool to crystallise the recent
// transcript into the kernel draft. Under the hood it runs the SAME
// extraction sub-agent the main chat's /save-kernel uses (with its
// test_kernel validation loop), then loads the validated JSON into the
// draft — it does NOT save (the user reviews + clicks Save). This is
// why the editor is robust: the kernel is built by the dedicated
// extractor, not hand-assembled inline.
export function createCommitToKernelTool({
    getDraft,
    setDraft,
    getMessages,
    onExtractionState,
    runContext = null,
}) {
    return {
        name: "commit_to_kernel",
        label: "Commit analysis to kernel",
        description:
            "Crystallise your recent in-chat analysis into this kernel. Call this AFTER you've produced the result the user wants — i.e. after a successful `render_block` (and the `analytics_query` / `execute_python` steps that fed it). It reads the recent transcript, builds the kernel's steps + render via a dedicated extraction pass with its own validation loop, and loads the result into the draft. " +
            "Use this as the main way to BUILD or REBUILD a kernel from an analysis. For small tweaks to an already-built kernel (swap render kind, rename a step), use the direct edit tools (update_render, update_step, …) instead. " +
            "Returns the resulting kernel summary on success, or an error describing what was missing (e.g. no render_block in the transcript) so you can fix it and retry.",
        parameters: Type.Object({
            title: Type.Optional(Type.String({ description: "Human-readable title for the kernel. Omit to let the extractor pick one from the analysis." })),
            feedback: Type.Optional(Type.String({ description: "Optional guidance for the extraction (e.g. 'parameterise the day window', 'the previous build double-counted retries'). Use when rebuilding after the user was unhappy with a prior result." })),
        }),
        async execute(_id, { title, feedback }) {
            const k = getDraft();
            const messages = typeof getMessages === "function" ? getMessages() : [];
            const transcript = summariseRecentMessages(messages);
            const extraction = await runKernelExtraction({
                transcript,
                command: k.command,
                title: title || k.title,
                description: k.description,
                feedback,
                // When rebuilding (feedback present), the current draft
                // IS the prior result the feedback is about — hand it to
                // the extractor so it can diff rather than re-derive blind.
                priorKernel: feedback ? k : undefined,
                runContext,
                onState: onExtractionState,
            });
            if (!extraction.ok) {
                return errResult("commit_to_kernel", extraction.error || "extraction failed", {
                    hint: "Make sure your analysis ended in a successful render_block before committing.",
                });
            }
            // Preserve the draft's command (identity is fixed by the
            // tab) and keep any description the user already wrote.
            const next = {
                ...extraction.kernel,
                command: k.command,
                ...(k.description && !extraction.kernel.description ? { description: k.description } : {}),
            };
            setDraft(next);
            return okResultWithKernel("commit_to_kernel", "crystallised the analysis into the kernel draft. Verify with run_kernel, then tell the user it's ready to review + save.", next, { op: "commit_to_kernel" });
        },
    };
}

// Limit each output to a small preview so the agent gets shape +
// sample rows without paying the full payload cost.
function previewValue(value, maxRows = 10) {
    if (value == null) return value;
    if (Array.isArray(value)) {
        return {
            _type: "array",
            length: value.length,
            sample: value.slice(0, maxRows),
        };
    }
    if (typeof value === "object") {
        if (Array.isArray(value.rows)) {
            return {
                _type: "table",
                columns: value.columns || null,
                rowCount: value.rows.length,
                sampleRows: value.rows.slice(0, maxRows),
            };
        }
        return value;
    }
    return value;
}

function serializeOutputs(outputs) {
    if (!outputs || typeof outputs.forEach !== "function") return null;
    const out = {};
    outputs.forEach((value, key) => { out[key] = previewValue(value); });
    return out;
}

// ── Factory — bundle the whole family ────────────────────────────

// inspect_kernel is intentionally NOT included. The kernel state is
// embedded in the system prompt and echoed back in every mutation
// tool result, so a dedicated "read state" tool is redundant — and
// in practice it acts as a behavioral attractor that ladders the
// model into "let me check the state…" loops it can't escape. The
// factory is still exported for tests / other callers that want it.
export function createKernelEditTools({
    getDraft,
    setDraft,
    getMessages,
    onExtractionState,
    resolvedDefinition = null,
    runContext = null,
}) {
    return [
        // Primary build path — crystallise an in-chat analysis into
        // the kernel via the dedicated extractor. Listed first so the
        // model reaches for it as the default "encode my work" move.
        createCommitToKernelTool({
            getDraft,
            setDraft,
            getMessages,
            onExtractionState,
            runContext,
        }),
        // Direct edit tools — for incremental tweaks to an existing
        // kernel without re-running an analysis.
        createUpdateMetaTool({ getDraft, setDraft }),
        createUpdateStepTool({ getDraft, setDraft }),
        createAddStepTool({ getDraft, setDraft }),
        createRemoveStepTool({ getDraft, setDraft }),
        createMoveStepTool({ getDraft, setDraft }),
        createUpdateRenderTool({ getDraft, setDraft }),
        createUpdateRenderSourceTool({ getDraft, setDraft }),
        createRunKernelTool({ getDraft, resolvedDefinition, runContext }),
    ];
}
